/**
 * How a weapon looks and how it moves through a swing.
 *
 * Castlevania's weapons are not held — they *appear*, describe a specific
 * motion, and vanish. That is what makes each one feel different before you
 * read a single stat: the axe goes over the head, the spear stabs, the whip
 * unfurls backwards and cracks forward. A weapon that is simply carried and
 * rotated reads as one weapon with different numbers.
 *
 * So a weapon's identity here is a **motion** first. The arc is authored; the
 * shape is derived.
 *
 * **That sentence used to end "not a picture", and #113 is the measurement of
 * what it cost.** It is right about where identity comes from and it was being
 * read as a licence to have no picture at all: ten motions were authored
 * against *one* drawing — a disc stamped along the run with a fixed linear
 * taper to half thickness, identical for every weapon in the catalogue — so the
 * whole shape vocabulary was one primitive and three scalars, and a sword, an
 * axe and a greatblade were literally the same object at 1.10 / 1.15 / 1.35
 * length. The playtest verdict was that weapons "don't really seem to match the
 * shape of what they represent", and the one weapon that did read was the
 * anchor, which escaped by accident: it is the only melee entry with internal
 * structure, and it got that because a chain needed `segments`.
 *
 * The shape is still *derived* rather than drawn by hand. What changed is what
 * it is derived from: a per-class **profile** (below) instead of a single
 * constant. A motion still has to be authored to be a family; a picture now
 * only has to be *specified*, in three to eleven keyframes.
 *
 * ---------------------------------------------------------------------------
 * **WHICH PROPERTY DRIVES WHICH CHANNEL.** One property per channel, and it is
 * written here because #113 asked for it to be stated: an arbitrary mapping is
 * forgotten and then contradicted.
 *
 *   - **class → shape.** `style` (which motion) and `profile` (which outline).
 *     What kind of thing the weapon is. Both come from one row of
 *     `MELEE_FAMILY`, which is #111's parametric family: read the note there
 *     before adding a class, and before assuming any two styles can be merged.
 *   - **reach → length, weight → thickness.** How big it is. Both are
 *     compressed (see `visualForClass`); neither touches colour.
 *   - **damage type → material → colour.** What it deals. A fire blade is
 *     literally lava, a frost blade is ice. `bladeMatForDamage` below owns that
 *     table and is the only thing here that decides a colour.
 *
 * **#85 adds the same three channels for what leaves the weapon**, in the
 * `rounds` section below, and the mapping is deliberately parallel: **class →
 * `RoundProfile`** (the outline), **speed → run length** (how big, via
 * `roundRunLength`, and unlike `reach` it is *derived* rather than compressed
 * because the derivation is a measurement), and **damage type → the same
 * `bladeMatForDamage` table** (the colour), so a fire round and a fire sword are
 * one element and not two palettes. What a round has and a blade does not is a
 * forward: its run is anchored to its velocity, not to a grip.
 *
 * **And nothing else tints.** No second colour channel is layered on the
 * material, and the refusal is measured rather than tasteful: the twelve
 * element materials already occupy the luminance line at a *median adjacent gap
 * of 17.5/255*, so ten of the eleven neighbouring pairs are inside the
 * project's standing 40/255 bin and ACID and ICE are 1.7 apart. A second
 * property read off the same channel would have to move a blade by at least the
 * bin to be seen at all, and `tests/weaponSilhouette.test.ts` §3 shows by
 * exhaustive scan that **every such move lands inside another element's bin**.
 * The channel is full. A tint carrying hue rather than value is not an escape
 * either, because the project's standing definition of "different" is a
 * luminance step (#60, #79, `tests/materialGrain.test.ts`) and a hue-only tint
 * is scored by it at zero.
 *
 * Geometry is in body units (the same units as `pixelBody` bones), and is
 * deliberately large — a weapon smaller than the character is invisible in
 * motion at this resolution.
 *
 * **The three axes a motion varies on**, which is the vocabulary `docs/22`
 * measured SotN against and the frame the rest of this file is written in:
 *
 *   - **path** — the angle and extension curves. Six of these shipped first.
 *   - **pivot** — the hand, or a point on the body. Added by #88: three of the
 *     nine families docs/22 §3 catalogued (rotary, nova, halo orbit) turn about
 *     the figure rather than the grip, and were not inexpressible because they
 *     were badly tuned — there was no field that could say it.
 *   - **passes** — how many times the weapon crosses the body's forward axis.
 *     Added by #89: SotN uses 0, 1 and 3, and every curve here used exactly 1.
 *
 * What is *not* varied, on purpose, is the body. One character animation serves
 * every weapon: with several character shapes planned, per-weapon body
 * animation costs shapes × weapons while an anchored weapon costs shapes +
 * weapons. `pixelBody.ts`'s `Skeleton.handX` states the split — the skeleton
 * owns where the weapon leaves the body, this file owns which way it points —
 * and `tests/weaponMotion.test.ts` holds it across all ten curves, including
 * the body-pivoted ones, which are the ones most likely to erode it.
 */

import { materialDef, type MatId } from '../sim/materials';
import { MAT } from '../sim/materialData';

export type SwingStyle =
  // ---- pivoted at the hand -------------------------------------------------
  | 'arc' // over the head and down — sword, axe, greatblade
  | 'slam' // overhead to straight down, heavy — hammer
  | 'thrust' // juts straight out and retracts — spear, lance
  | 'jab' // short, fast, low — dagger, claw, gauntlet
  | 'whip' // unfurls backward, then cracks forward — whip
  | 'spin' // full rotation — staff, drill
  | 'flurry' // three passes inside one motion — relicBlade (#89)
  // ---- pivoted on the body -------------------------------------------------
  //
  // Read the note on `bodyPivot` before adding to this group. #91 measured a
  // second, longer source and found that SotN does not displace a *weapon's*
  // pivot at all — it spawns an effect with its own centre while the weapon
  // stays at the hand. So these three are what a displaced pivot is honestly
  // good for, ranked, rather than three reproductions of equal standing.
  | 'rotary' // orbits the whole figure, several turns — scythe (#88)
  | 'nova' // erupts radially from the torso and retracts — living, [ours]
  | 'halo' // circles the figure at a fixed radius — chain (#88)
  // ---- draws nothing -------------------------------------------------------
  | 'none'; // shields and projectiles draw nothing

/**
 * The outline of the thing, as distinct from the motion it describes.
 *
 * A profile is **half-width as a function of position along the run**, in
 * multiples of `thickness`, `0` at the grip and `1` at the tip. The renderer
 * already walks that run stamping one primitive per step; before #113 it scaled
 * every stamp by the same `1 - along * 0.5`, which is the `taper` entry below
 * and is why every weapon was a stick. Everything else here is that one
 * expression given a shape.
 *
 * **Why a procedural profile and not an authored bitmap mask.** #113 put both
 * on the table. A mask buys genuinely arbitrary silhouettes and costs three
 * things a profile does not: an asset per weapon, an editor to author them
 * (#115, which is explicitly gated on this outcome), and a **pre-rotation
 * story** — the drawing is rotated continuously through a swing, so a mask
 * would need the treatment #42 gave rigid bodies or it aliases badly at every
 * angle that is not a multiple of 90°. A profile is rotation-free by
 * construction, because it is resolved along the weapon's own axis and the axis
 * is what gets rotated. So this is the cheap half, done first, and the question
 * it exists to answer is empirical: if a hammer with a real head still does not
 * read as a hammer, *that* is the evidence that buys the pipeline.
 *
 * **What a half-width alone cannot express, said plainly:** the run is a line,
 * so a half-width is symmetric about it. A weapon whose mass sits to one side —
 * a real axe bit, a scythe's blade — could only be approximated by a swelling
 * *on* the axis, and `crescent` and `bearded` were that approximation rather
 * than the shape. This paragraph used to end "an off-axis lobe is the first
 * thing a mask would buy", and #115 took it up: see `profileOffsetAt` below,
 * where the lobe is a second keyframe curve rather than a mask, and read that
 * note before reaching for an asset pipeline. A profile is now a *band* — a
 * half-width and a centre — and only its centre curve was ever missing.
 */
export type WeaponProfile =
  // ---- the fallback --------------------------------------------------------
  | 'taper' // the pre-#113 drawing: uniform linear taper to half thickness
  // ---- bladed --------------------------------------------------------------
  | 'blade' // thin grip, a guard, then a long taper to a point — sword
  | 'leaf' // widest two thirds along, both ends narrow — relicBlade
  | 'crescent' // slim snath, a neck, a wide flare — scythe
  | 'fang' // short, broad at the base, gone by the tip — dagger
  | 'talons' // a wrist and three swells at the business end — claw
  // ---- hafted, mass at the end ---------------------------------------------
  | 'bearded' // thin haft, mass in the last quarter — axe
  | 'block' // thin haft, a square head that does not taper — hammer
  | 'fluke' // a slim run, then the heaviest head in the catalogue — anchor
  | 'fist' // stubby, blunt, no point at all — gauntlet
  // ---- poled ---------------------------------------------------------------
  | 'spike' // slim shaft, socket collar, needle point — spear
  | 'couched' // a vamplate near the grip, then a long cone — lance
  | 'shaft' // near-uniform with a slight waist swell — staff
  | 'auger' // a flighted cone: ripples down to a point — drill
  // ---- flexible ------------------------------------------------------------
  | 'cord' // thin, near-uniform, thinning to a lash — whip
  | 'linked' // repeating beads and a flail head — chain
  | 'sinew'; // an organic limb: thick at the root, peristaltic — living

/**
 * Each profile as keyframes `[along, halfWidth]`, linearly interpolated.
 *
 * Keyframes rather than closures because the set has to be *comparable*: the
 * separation test below measures every class's drawn silhouette against every
 * other's, and a table can be read, diffed and argued about in review. Values
 * are multiples of `thickness`, so a profile says the shape and `thickness`
 * says the size — the two channels stay separate, which is the point of the
 * mapping in the header.
 *
 * Above 1.0 is deliberate and is most of what makes a head read as a head:
 * `thickness` is calibrated on the *shaft*, so an axe bit at 1.6 is 1.6× the
 * haft rather than 1.6× some average nobody can see.
 */
const PROFILE_KEYS: Readonly<Record<WeaponProfile, readonly (readonly [number, number])[]>> = {
  // The drawing every weapon had before #113. Kept, because `profileAt` has to
  // be total and the honest fallback for an unknown profile is the thing that
  // used to ship — and because `tests/weaponSilhouette.test.ts` asserts that no
  // drawn class still resolves to it, which is a claim that needs the entry to
  // exist in order to be false.
  taper: [[0, 1], [1, 0.5]],

  blade: [[0, 0.42], [0.07, 0.5], [0.11, 1.3], [0.15, 0.68], [0.26, 0.86], [1, 0.08]],
  leaf: [[0, 0.4], [0.08, 0.46], [0.12, 0.9], [0.62, 1.15], [0.86, 0.6], [1, 0.08]],
  crescent: [[0, 0.34], [0.64, 0.3], [0.71, 0.6], [0.78, 1.6], [0.9, 1.15], [1, 0.14]],
  fang: [[0, 0.5], [0.12, 1.2], [0.34, 0.88], [1, 0.05]],
  talons: [[0, 0.95], [0.28, 0.6], [0.44, 1.6], [0.58, 0.5], [0.72, 1.5], [0.85, 0.5], [1, 0.08]],

  bearded: [[0, 0.42], [0.7, 0.38], [0.755, 1.65], [0.94, 1.5], [1, 0.5]],
  block: [[0, 0.4], [0.74, 0.36], [0.775, 1.4], [0.97, 1.4], [1, 1.25]],
  fluke: [[0, 0.28], [0.62, 0.28], [0.7, 0.85], [0.8, 1.8], [0.95, 1.35], [1, 0.45]],
  fist: [[0, 0.5], [0.42, 0.56], [0.54, 1.4], [0.92, 1.25], [1, 0.95]],

  spike: [[0, 0.55], [0.1, 0.66], [0.8, 0.5], [0.87, 0.82], [1, 0.04]],
  couched: [[0, 0.5], [0.1, 0.66], [0.15, 1.55], [0.31, 1.3], [0.36, 0.72], [0.6, 0.58], [1, 0.05]],
  shaft: [[0, 0.78], [0.06, 1.1], [0.13, 0.84], [0.5, 1.02], [0.87, 0.84], [0.94, 1.1], [1, 0.78]],
  auger: [
    [0, 0.5], [0.1, 0.56], [0.18, 1.4], [0.27, 0.48], [0.4, 1.25],
    [0.52, 0.44], [0.66, 1.05], [0.78, 0.4], [0.9, 0.62], [1, 0.04],
  ],

  cord: [[0, 0.95], [0.55, 0.62], [1, 0.3]],
  linked: [
    [0, 0.45], [0.09, 1.25], [0.18, 0.4], [0.29, 1.25], [0.38, 0.4],
    [0.49, 1.25], [0.58, 0.4], [0.69, 1.25], [0.78, 0.4], [0.89, 1.7], [1, 0.5],
  ],
  sinew: [[0, 0.85], [0.22, 1.35], [0.55, 1.0], [0.8, 0.72], [1, 0.1]],
};

/**
 * Half-width at `along` (0 = grip, 1 = tip), as a multiple of `thickness`.
 *
 * The renderer multiplies this by `thickness * scale` and clamps the result at
 * `MIN_HALF_WIDTH`, so a profile is free to go to nearly zero at a point: the
 * clamp is what keeps a tip drawn at all, and it is also why the separation
 * test compares *clamped* widths. Two shapes that differ by less than the
 * smallest primitive the rasteriser can place do not differ.
 */
export function profileAt(profile: WeaponProfile, along: number): number {
  // `hasOwnProperty` and not `?? PROFILE_KEYS.taper`, for the reason
  // `visualForClass` states about `MELEE_FAMILY`: an object index is not total.
  // `PROFILE_KEYS['toString']` is a *function*, which is not nullish, so the
  // fallback never fired and the walk below indexed `keys[-1][1]` and threw.
  // Nothing reachable passes a bad profile today — `WeaponVisual.profile` is
  // typed and `visualForClass` guards its own lookup — but a renderer that can
  // throw once per stamp is not a risk worth carrying for one call to
  // `hasOwnProperty`, and `tests/weaponLobe.test.ts` found this by asking.
  const keys = Object.prototype.hasOwnProperty.call(PROFILE_KEYS, profile)
    ? PROFILE_KEYS[profile]
    : PROFILE_KEYS.taper;
  const t = along < 0 ? 0 : along > 1 ? 1 : along;
  for (let k = 1; k < keys.length; k++) {
    const [x1, w1] = keys[k];
    if (t <= x1) {
      const [x0, w0] = keys[k - 1];
      const span = x1 - x0;
      return span <= 0 ? w1 : w0 + (w1 - w0) * ((t - x0) / span);
    }
  }
  return keys[keys.length - 1][1];
}

/**
 * How far the outline sits **off** the run, at `along`, in multiples of
 * `thickness`. Positive is the weapon's back — the side the haft is not on.
 *
 * ==== THE ONE THING A PROFILE COULD NOT SAY =============================
 *
 * `WeaponProfile` above records what it cannot express, and says it in the
 * plainest terms available: *"the run is a line, so a profile is symmetric
 * about it. A weapon whose mass sits to one side — a real axe bit, a scythe's
 * blade — is approximated by a swelling on the axis. `crescent` and `bearded`
 * are that approximation and not the shape itself. An off-axis lobe is the
 * first thing a mask would buy."*
 *
 * Issue #115 closes on that sentence: the profile carried the silhouette, the
 * editor is not rescuing a failed approach, and **an off-axis lobe is the
 * concrete thing an editor buys for weapons**. This is that lobe, and it is not
 * a mask. A mask would cost an asset per weapon and a pre-rotation story — the
 * treatment `rigidShapes.ts` gives a crate, for an object that is rotated
 * continuously through a swing rather than to one of 32 stops. What the axe
 * actually needed was a *second* scalar per keyframe, and the run's own
 * perpendicular is already sitting there unused.
 *
 * So the drawing is now a band, not a tube: half-width `profileAt`, centred at
 * `profileOffsetAt` rather than at zero. `bearded` and `crescent` stop being
 * on-axis swellings and become an axe bit and a scythe blade — mass to one
 * side, haft running straight through.
 *
 * ==== WHY THIS DOES NOT DRIFT FROM THE HITBOX ===========================
 *
 * It moves ink and nothing else. The spine `swingSpine` walks is what the
 * hitbox is derived from and it is untouched, so a lobe cannot make a weapon
 * damage along a path it does not draw — the defect #85 found in eight of
 * eleven projectile classes, in the opposite direction. What the lobe *can* do
 * is draw ink the hitbox does not cover, which is why `tests/weaponLobe.test.ts`
 * bounds the offset against the half-width rather than letting it run free.
 *
 * ==== WHY MOST CLASSES ARE ZERO, AND STAY ZERO ==========================
 *
 * A spear is symmetric about its shaft. A staff, a hammer, a chain, a whip and
 * a fist all are. Authoring a lobe on them would be a second way to say
 * "nothing" and would put every class at risk of a rounding difference in a
 * table nobody meant to change. Four classes are asymmetric objects in the
 * world and those four carry an offset; the other thirteen resolve through the
 * empty-keys fast path below and are bit-identical to their pre-lobe drawing,
 * which `tests/weaponLobe.test.ts` §1 pins.
 */
const PROFILE_OFFSET: Readonly<Partial<Record<WeaponProfile, readonly (readonly [number, number])[]>>> = {
  // The axe. Haft dead straight to 0.70, then the bit hangs off the back — it
  // reaches 1.05 while the half-width there is 1.65, so the bit still crosses
  // the haft and the two are one object rather than a blade floating beside a
  // stick. The beard (the downward hook a bearded axe is named for) is the
  // 0.94→1.0 fall, where the offset stays out while the width collapses.
  bearded: [[0, 0], [0.7, 0], [0.78, 0.95], [0.94, 1.05], [1, 0.8]],

  // The scythe. The snath is straight, the neck turns, and the blade sweeps out
  // further than the axe's bit does because a scythe's blade *is* the weapon —
  // there is nothing on the axis past 0.71 but a mounting.
  crescent: [[0, 0], [0.62, 0], [0.72, 0.5], [0.8, 1.5], [0.9, 1.55], [1, 1.1]],

  // The claw. Three swells on one side of a wrist: talons curl, and a talon
  // that curls symmetrically is a drill bit. Small offsets — the hand is at the
  // grip and the whole object is short.
  talons: [[0, 0], [0.3, 0.1], [0.44, 0.55], [0.58, 0.35], [0.72, 0.6], [1, 0.45]],

  // The living limb. `sinew` is peristaltic along its length and an organic
  // limb does not grow straight; the offset is a single slow lean rather than a
  // head, which is what distinguishes it from the four hafted profiles.
  sinew: [[0, 0], [0.3, 0.25], [0.62, 0.55], [1, 0.35]],
};

/**
 * Lateral offset at `along`, in multiples of `thickness`. `0` for any class
 * that has not authored one, which is thirteen of the seventeen.
 *
 * Same keyframe walk and the same clamping as `profileAt`, deliberately: two
 * curves sampled the same way at the same `along` cannot drift apart, and the
 * renderer resolves both in one expression.
 */
export function profileOffsetAt(profile: WeaponProfile, along: number): number {
  // See `profileAt`: `PROFILE_OFFSET['toString']` is inherited and truthy.
  if (!Object.prototype.hasOwnProperty.call(PROFILE_OFFSET, profile)) return 0;
  const keys = PROFILE_OFFSET[profile];
  if (keys === undefined) return 0;
  const t = along < 0 ? 0 : along > 1 ? 1 : along;
  for (let k = 1; k < keys.length; k++) {
    const [x1, w1] = keys[k];
    if (t <= x1) {
      const [x0, w0] = keys[k - 1];
      const span = x1 - x0;
      return span <= 0 ? w1 : w0 + (w1 - w0) * ((t - x0) / span);
    }
  }
  return keys[keys.length - 1][1];
}

/** Which classes draw off-axis. Exported so a sweep can label them without guessing. */
export const OFFSET_PROFILES: readonly WeaponProfile[] = Object.keys(PROFILE_OFFSET) as WeaponProfile[];

/**
 * The smallest half-width the weapon rasteriser can place, body units.
 *
 * `game.ts`'s `drawWeapons` clamps every stamp to this; it is stated here
 * because it is a property of the drawing and because the silhouette test needs
 * the same number to know which differences are real. A difference below it is
 * a difference the renderer cannot express.
 */
export const MIN_HALF_WIDTH = 0.55;

/**
 * World units between consecutive stamps along a weapon or a round.
 *
 * One number, stated once, because it had been written out as a bare `1.4` in
 * five places: `drawWeapons`, `drawProjectiles`, `roundStamps` below,
 * `tools/view/weapon.mjs` and `tests/weaponLobe.test.ts` — and it is the
 * cadence at which "the blade and the stick are one object" is either true or
 * false, which `tests/weaponLobe.test.ts` asserts and the offline sweep draws.
 * A viewer that sampled a swing at a different cadence than the game would draw
 * a weapon with gaps the game does not have, or hide gaps the game does; that
 * is `tests/weaponSweep.test.ts`'s defect exactly, in a constant rather than in
 * a formula.
 *
 * `roundStamps` picks it up here. The two literals in `src/core/game.ts` are
 * the hunk `docs/25-pixel-tools.md` §6 owes that file, which is reserved.
 */
export const STAMP_CADENCE = 1.4;

/**
 * Render scale for weapon and round stamping — #148, the same move #147 makes
 * for `pixelBody.ts`'s atlas, applied independently to this file's mechanism.
 *
 * There is no texture here: a weapon or a round is a run of `Shape.RECT`
 * quads placed and sized in world units directly, so "one texel per world
 * unit" names the *sampling cadence* (`STAMP_CADENCE`, 1.4 units between
 * stamps) rather than a pixel grid. `K` scales that cadence down —
 * `stampCadenceAt` — so a run is subdivided into more, smaller stamps that
 * trace the profile curve more closely, while every stamp is still placed and
 * sized in world units: the drawn extent does not move, only how finely it is
 * sampled. That is "rasterise at K, draw at world size" with no texture to
 * bake into.
 *
 * **A local constant, deliberately, not a shared render-scale surface.** #147
 * may land its own `K` for the body atlas in parallel; this one is scoped to
 * `stampCadenceAt`/`roundStamps` only and is named for this file's mechanism
 * so the two are trivially reconcilable into one constant later if that turns
 * out to make sense, rather than this file guessing at #147's shape now.
 *
 * Default `1`, which must leave every stamp exactly where it already was —
 * `tests/projectileSilhouette.test.ts` and `tests/weaponLobe.test.ts` hold
 * that. Weapons and rounds are brief and fast-moving, so this is a smaller
 * win than the body atlas and is not tuned above `1` here; it exists so a
 * later change can raise it without re-deriving the mechanism.
 */
export const WEAPON_RENDER_SCALE = 1;

/**
 * `STAMP_CADENCE` at render scale `k`: world units between consecutive
 * stamps. `k` doubles the stamp density by halving the gap; it never changes
 * a stamp's own position or size, both of which stay in world units, so the
 * drawn silhouette's *extent* is invariant in `k` and only its *smoothness*
 * moves. `k <= 0` is treated as `1` — a render scale cannot invert the
 * cadence or divide by zero.
 */
export function stampCadenceAt(k: number = WEAPON_RENDER_SCALE): number {
  return STAMP_CADENCE / (k > 0 ? k : 1);
}

/**
 * How many stamps to lay along a run of world length `len`, at render scale
 * `k`. The one place `drawWeapons`' spine walk and the tether rope compute
 * their step count, so the two cannot sample at different cadences the way
 * `STAMP_CADENCE`'s own header warns about. Always at least 1: a run shorter
 * than the cadence still gets a stamp.
 */
export function stampSteps(len: number, k: number = WEAPON_RENDER_SCALE): number {
  return Math.max(1, Math.ceil(len / stampCadenceAt(k)));
}

/** One stamp of the weapon outline: where it goes, and how big it is. */
export interface WeaponStamp {
  x: number;
  y: number;
  /** Half-extent, already clamped to `MIN_HALF_WIDTH`. */
  r: number;
}

/** A reusable stamp. `drawWeapons` runs this loop every frame and allocates nothing. */
export function makeWeaponStamp(): WeaponStamp {
  return { x: 0, y: 0, r: 0 };
}

/**
 * Resolve one stamp of the outline, in world units.
 *
 * **Why this is a function here and not three lines in `drawWeapons`.** It used
 * to be three lines in `drawWeapons`, and that was fine while the answer was
 * "the point on the segment, at the profile's half-width". The lobe makes the
 * *position* profile-dependent too, and this file already carries the scar from
 * the last time a piece of the weapon's geometry lived in the renderer:
 * `swingSpine` was lifted into `wielder.ts` precisely so that
 * `tests/drawnAim.test.ts` "measures the tip angle the renderer produces rather
 * than a copy of the arithmetic that produces it — which is exactly how #112's
 * rotation and `fireOrSwing`'s came to mean different things". A second copy of
 * the offset arithmetic in a test or in `tools/pixel.mjs` would be the same
 * mistake with a new name, and the offline viewer exists to be trusted.
 *
 * `ax,ay → bx,by` is the current spine segment, `t` the position along it, and
 * `along` the position along the *whole* run — they differ for a multi-segment
 * weapon, and the profile is resolved against the run.
 *
 * **`face` mirrors the lobe with the character.** The perpendicular is taken
 * from the already-mirrored segment direction, and mirroring flips handedness,
 * so without the `face` factor an axe swung leftwards would grow its bit on the
 * wrong side of the haft — the drawing would not be the mirror image of itself.
 */
export function stampAt(
  profile: WeaponProfile,
  thickness: number,
  scale: number,
  face: number,
  along: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  t: number,
  out: WeaponStamp,
): void {
  const unit = thickness * scale;
  out.r = Math.max(MIN_HALF_WIDTH, unit * profileAt(profile, along));
  let x = ax + (bx - ax) * t;
  let y = ay + (by - ay) * t;
  const off = profileOffsetAt(profile, along);
  if (off !== 0) {
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy);
    if (len > 0) {
      // (−dy, dx) / len is the run's left normal; `face` mirrors it.
      const d = unit * off * (face < 0 ? -1 : 1);
      x += (-dy / len) * d;
      y += (dx / len) * d;
    }
  }
  out.x = x;
  out.y = y;
}

export interface WeaponVisual {
  style: SwingStyle;
  /** Full extension, body units. */
  length: number;
  thickness: number;
  mat: MatId;
  /** Polyline segments. >1 gives a flexible weapon (whip, chain). */
  segments: number;
  /**
   * The outline, resolved along the run. See `WeaponProfile`.
   *
   * This is the field that used to be missing, and `segments` is what people
   * reached for instead — which is why the anchor is the only weapon that
   * reads. `segments` buys *flexibility* (it is what `curl` bends), not
   * structure; a sword is rigid and still needs a guard and a point.
   */
  profile: WeaponProfile;
}

// =========================================================== rounds (#85)
//
// Everything above draws a weapon in a hand. Everything below draws what leaves
// it. They share this file because they share the mechanism — a profile along a
// run — and because they must share `bladeMatForDamage`: a fire round and a fire
// sword are the same element and cannot be two palettes.

/**
 * The outline of a round in flight, as distinct from the weapon that fired it.
 *
 * **The defect, measured.** `drawProjectiles` stamped one `Shape.CIRCLE` per
 * round at `max(1.2, p.radius[k])`, coloured by damage type. `p.radius` is the
 * *contact* radius, and across the eleven weapon classes that actually put a
 * round in the pool it takes six values — 2.0, 2.5, 3.0, 3.5, 6.0 and (a bug,
 * see `fireFromWeapon`) 45.0. So the entire shape vocabulary of every projectile
 * in the game was one primitive and one scalar, and four of the eleven — the
 * Service Repeater, the Longbore Rifle, the Silent Recurve and the Prism
 * Shardcaster, a sidearm, an anti-materiel rifle, a bow and a crystal caster —
 * drew the **identical pixel** at radius 2, ten of the fifty-five class pairs
 * being pixel-identical in all. That is #113's finding one file over, unfixed,
 * and it is why the projectile half of #85 could not be judged.
 *
 * **What "Metroid" adds that the melee vocabulary does not have, and it is one
 * thing.** A blade has a grip, so its run is anchored to the hand and its
 * orientation comes from the swing. A round has *velocity*, so its run is
 * anchored to where it is going — and that is the axis the drawing threw away
 * completely. `SpriteBatch.push` has carried a `rot` attribute and independent
 * `halfW`/`halfH` since it was written; `drawProjectiles` passed `r, r` and let
 * `rot` default to zero. Every round in the game was a direction-free dot.
 *
 * So a round profile is **half-width as a function of position along the travel
 * axis**, in multiples of the contact radius, `0` at the tail and `1` at the
 * nose. Asymmetric on purpose — that is the whole difference from
 * `WeaponProfile`, which is anchored at a grip and has no forward.
 *
 * **Never above 1.0**, which `WeaponProfile` deliberately allows and this
 * deliberately does not: there, `thickness` is calibrated on the shaft so a head
 * at 1.6 means something; here the multiplier is the radius the round *hits* at,
 * so a profile above 1 would draw a round larger than its own hitbox. Below 1 is
 * allowed and is how a rail bolt is a thin bright line that still connects
 * generously. `tests/projectileSilhouette.test.ts` §2 holds the cap.
 */
export type RoundProfile =
  // ---- the fallback --------------------------------------------------------
  //
  // The drawing every round had. Kept because `roundProfileAt` has to be total,
  // because the before/after in `tests/projectileSilhouette.test.ts` §1 needs
  // the old drawing to still exist in order to fail against it, and because it
  // is the honest shape for an enemy attack, which has no weapon class to look
  // up — see `fireFlat`.
  | 'round'
  // ---- kinetic -------------------------------------------------------------
  | 'slug' // blunt cylinder, rounded nose — pistol
  | 'needle' // thin, uniform, sharp — rifle
  | 'pellet' // a ball, slightly squat — scattergun
  | 'arrow' // nock flare, thin shaft, broadhead, point — bow
  | 'grapnel' // a long thin chain and the heaviest head in flight — anchor
  // ---- ordnance ------------------------------------------------------------
  | 'shell' // tail fins, waist, fat ogive — launcher
  | 'bundle' // two lumps on a strap — thrownRig
  // ---- energy --------------------------------------------------------------
  | 'streak' // nothing at the tail, a small head at the nose — rail
  | 'sliver' // a thin lens, pointed both ends — shardCaster
  // ---- matter --------------------------------------------------------------
  | 'droplet' // long thin tail, mass at the nose — sprayer
  | 'pod'; // a seed body on a short stalk — bio

/**
 * Every profile, in wire order. **The index is the id stored on the round**, so
 * this array may be appended to and must never be reordered: `Projectiles`
 * keeps a `Uint8Array` column rather than a class string, because
 * `projectiles.ts` states that its object columns are "written on spawn, read on
 * contact, never per-step" and the renderer reads this every frame.
 */
export const ROUND_PROFILES: readonly RoundProfile[] = [
  'round',
  'slug', 'needle', 'pellet', 'arrow', 'grapnel',
  'shell', 'bundle',
  'streak', 'sliver',
  'droplet', 'pod',
];

/**
 * Keyframes `[along, halfWidth]`, tail to nose, linearly interpolated —
 * the same representation and the same reasons as `PROFILE_KEYS`.
 */
const ROUND_KEYS: Readonly<Record<RoundProfile, readonly (readonly [number, number])[]>> = {
  // A circle, sampled. This is the drawing that shipped: at a run of one
  // diameter it is the `Shape.CIRCLE` quad `drawProjectiles` used to push, and
  // §1's "before" row is this profile at that run.
  round: [
    [0, 0], [0.05, 0.436], [0.1, 0.6], [0.2, 0.8], [0.3, 0.917], [0.5, 1],
    [0.7, 0.917], [0.8, 0.8], [0.9, 0.6], [0.95, 0.436], [1, 0],
  ],

  slug: [[0, 0.45], [0.06, 0.66], [0.16, 0.7], [0.74, 0.74], [0.88, 0.64], [1, 0.28]],
  needle: [[0, 0.28], [0.05, 0.5], [0.13, 0.4], [0.8, 0.38], [0.93, 0.5], [1, 0.03]],
  pellet: [[0, 0.1], [0.14, 0.66], [0.32, 0.94], [0.5, 1], [0.7, 0.94], [0.88, 0.62], [1, 0.08]],
  arrow: [
    [0, 0.5], [0.05, 0.78], [0.1, 0.26], [0.15, 0.66], [0.21, 0.24],
    [0.7, 0.22], [0.82, 0.88], [0.9, 0.62], [1, 0.04],
  ],
  grapnel: [[0, 0.22], [0.12, 0.24], [0.5, 0.2], [0.62, 0.52], [0.74, 1], [0.9, 0.88], [1, 0.28]],

  shell: [
    [0, 0.62], [0.07, 0.68], [0.14, 0.38], [0.32, 0.56], [0.52, 0.8],
    [0.74, 1], [0.9, 0.84], [1, 0.32],
  ],
  bundle: [
    [0, 0.18], [0.1, 0.76], [0.28, 0.9], [0.42, 0.38], [0.55, 0.48],
    [0.72, 0.96], [0.9, 0.8], [1, 0.22],
  ],

  streak: [[0, 0], [0.35, 0.16], [0.7, 0.26], [0.9, 0.44], [0.97, 0.34], [1, 0.08]],
  sliver: [[0, 0.02], [0.2, 0.3], [0.4, 0.56], [0.5, 0.64], [0.62, 0.56], [0.82, 0.3], [1, 0.02]],

  droplet: [[0, 0.04], [0.16, 0.2], [0.42, 0.34], [0.64, 0.62], [0.82, 0.96], [0.93, 1], [1, 0.42]],
  pod: [
    [0, 0.1], [0.1, 0.2], [0.24, 0.17], [0.36, 0.76], [0.56, 1],
    [0.78, 0.9], [0.92, 0.6], [1, 0.14],
  ],
};

/**
 * Half-width at `along` (0 = tail, 1 = nose), as a multiple of contact radius.
 *
 * Takes the **id**, not the name, because the renderer reads it off a
 * `Uint8Array` once per stamp. An id outside the table falls back to `round`
 * rather than throwing: a projectile that draws nothing is a projectile the
 * player cannot see coming.
 */
export function roundProfileAt(id: number, along: number): number {
  const keys = ROUND_KEYS[ROUND_PROFILES[id] ?? 'round'];
  const t = along < 0 ? 0 : along > 1 ? 1 : along;
  for (let k = 1; k < keys.length; k++) {
    const [x1, w1] = keys[k];
    if (t <= x1) {
      const [x0, w0] = keys[k - 1];
      const span = x1 - x0;
      return span <= 0 ? w1 : w0 + (w1 - w0) * ((t - x0) / span);
    }
  }
  return keys[keys.length - 1][1];
}

/** Wire id for a profile. `round` is 0, which is also the zero-filled default. */
export function roundProfileId(p: RoundProfile): number {
  const i = ROUND_PROFILES.indexOf(p);
  return i < 0 ? 0 : i;
}

/**
 * Which round a weapon class fires. Total, and `round` for anything unknown.
 *
 * `hasOwnProperty` for the reason `visualForClass` gives: `cls` arrives as a
 * plain string off the catalogue and `ROUND_BY_CLASS['toString']` is a function.
 */
const ROUND_BY_CLASS: Readonly<Record<string, RoundProfile>> = {
  pistol: 'slug',
  rifle: 'needle',
  scattergun: 'pellet',
  bow: 'arrow',
  anchor: 'grapnel',
  launcher: 'shell',
  thrownRig: 'bundle',
  rail: 'streak',
  shardCaster: 'sliver',
  sprayer: 'droplet',
  bio: 'pod',
};

export function roundProfileForClass(cls: string): RoundProfile {
  return Object.prototype.hasOwnProperty.call(ROUND_BY_CLASS, cls)
    ? ROUND_BY_CLASS[cls]
    : 'round';
}

/**
 * The longest a round may be drawn, as a multiple of its own diameter.
 *
 * A ceiling and not a target. `Projectiles.MAX_SPEED` is 4000 units/sec, which
 * at a 60 Hz step is 66.7 units of travel in one frame — three body heights —
 * and a modded speed multiplier can reach it. Ten diameters is past every
 * shipping class (the rail, the fastest thing in the catalogue at 2600, wants
 * 8.7) so nothing real is clipped, and it bounds the stamp count per round.
 */
export const MAX_ROUND_ASPECT = 10;

/**
 * The smallest radius a round is ever drawn at, in units.
 *
 * `game.ts`'s own floor, lifted here so the silhouette test measures the same
 * number the renderer applies. It is a floor on the *round*, not on a stamp —
 * the stamp floor is `MIN_HALF_WIDTH`, the same one `drawWeapons` clamps to,
 * because it is a property of the rasteriser and not of what is being drawn.
 */
export const MIN_ROUND_RADIUS = 1.2;

/**
 * How long to draw a round, in units along its own velocity.
 *
 * **This is the measured half of #85 and it is not a style choice.**
 * `Projectiles.step` integrates a round's full per-frame displacement and then
 * *sweeps* it in ~2-unit substeps, resolving a hit anywhere along the path. The
 * drawing showed the endpoint only. At the catalogue's own speeds, with a 60 Hz
 * step, the fraction of the damaging path that was drawn is:
 *
 *     rail        0.115      shardCaster  0.462      bio        0.789
 *     rifle       0.209      scattergun   0.556      thrownRig  0.857
 *     bow         0.343      launcher     1.235      sprayer    1.385
 *     pistol      0.387      anchor       2.571
 *
 * Eight of eleven classes drew less than the path they damaged along, and the
 * rail drew **11 %** of it: at 2600 units/sec it clears 43 units per step and
 * was drawn as a 5-unit dot, so it did not travel, it teleported eight of its
 * own lengths at a time. Drawing the run is therefore not motion blur added for
 * flavour — it is drawing the sweep that already exists. (The sprayer's 1.385
 * is post-`roundRadius`; before it, its droplets were radius 45 and it drew
 * **twenty times** its own sweep.)
 *
 * It is also exactly the beam/shot distinction: a fast round overlaps itself
 * frame to frame and reads as a line, a slow one does not and reads as an
 * object. One rule produces both, so there is no second `isBeam` field to keep
 * in sync with the speed that decides it.
 *
 * `stepDt` is passed rather than assumed so this file does not keep a second
 * copy of `game.ts`'s `SIM_HZ`. A round is never drawn shorter than its own
 * diameter, which is the length it had before.
 */
export function roundRunLength(speed: number, radius: number, stepDt: number): number {
  const dia = 2 * Math.max(MIN_ROUND_RADIUS, radius);
  const swept = Math.abs(speed) * (stepDt > 0 ? stepDt : 0);
  return Math.min(dia * MAX_ROUND_ASPECT, Math.max(dia, Number.isFinite(swept) ? swept : 0));
}

/** Stamps per round. The ceiling is the sprite budget; see `roundStamps`. */
export const MAX_ROUND_STAMPS = 12;

/**
 * How many primitives to lay along a round's run.
 *
 * `drawWeapons` steps a swing every 1.4 units and this uses the same cadence, so
 * a round and the blade beside it are drawn at one resolution. At the
 * catalogue's own numbers that gives 5 stamps for the shortest round (the
 * sprayer's droplet, 6 units) and would give 31 for the longest (the rail's
 * streak, 43).
 *
 * **The ceiling of twelve is arithmetic, not taste.** `Projectiles` holds 512
 * rounds and `SpriteBatch` holds 8192 instances, of which `drawProjectiles`
 * shares a batch with `drawEntities` only. 512 × 12 = 6144 leaves 2048. It binds
 * on exactly one class — the rail — and what that class loses is detail nothing
 * could read on a 43-unit object moving 2600 units a second.
 *
 * **There is no floor**, and the omission is deliberate: `ceil(run / 1.4)` on
 * the smallest round the game can produce (`MIN_ROUND_RADIUS`, 2.4 units long)
 * is 2, no shipping class comes near it, and a guard nothing can reach is the
 * dead rung `tests/weaponMotion.test.ts` refuses on principle — "a curve nothing
 * can equip is a curve nobody will ever see fail".
 *
 * The renderer must place **rectangles a step long**, not discs: discs `run / N`
 * apart leave gaps at the ceiling, and a rectangle tiles the run continuously at
 * any `N`. That is also why the count can be this low.
 *
 * **Why the round-length guard holds at every `k`.** `drawProjectiles` derives
 * each stamp's half-extent from this count as `run / (2 * roundStamps(run))`
 * and centres the `n` stamps back-to-back from the nose, so the tiled span is
 * `n * (run / n) = run` for *any* `n` this returns — `k` changes how many
 * pieces the run is cut into, never the sum of their lengths. A round is
 * therefore never drawn shorter than `run` — which `roundRunLength` already
 * pins to the swept path — regardless of `k`, including once `n` saturates at
 * `MAX_ROUND_STAMPS`. `tests/projectileSilhouette.test.ts` measures this
 * directly rather than trusting the arithmetic.
 */
export function roundStamps(run: number, k: number = WEAPON_RENDER_SCALE): number {
  const n = Math.ceil(run / stampCadenceAt(k));
  return n < 1 ? 1 : n > MAX_ROUND_STAMPS ? MAX_ROUND_STAMPS : n;
}

// ------------------------------------------------------------------- colour

/**
 * What a weapon is made of, by what it deals.
 *
 * A fire blade is literally lava, a frost blade is ice, a void blade is void
 * matter. This costs nothing — the palette already exists — and means the
 * element is readable off the silhouette before the first hit lands. Indexed by
 * `DamageType`; `tests/weaponSilhouette.test.ts` §3 pins the length against
 * `DAMAGE_TYPE_NAMES` so a thirteenth damage type cannot arrive uncoloured.
 */
const BLADE_MAT_BY_DAMAGE: readonly (keyof typeof MAT)[] = [
  'STEEL',          // physical
  'LAVA',           // fire
  'ICE',            // frost
  'CRYSTAL',        // shock
  'ACID',           // acid
  'PLAGUE_SLURRY',  // toxic
  'VOID_MATTER',    // void
  'RADIANT_MATTER', // radiant
  'CURSED_BLOOD',   // blood
  'CHRONO_FLUID',   // temporal
  'FUNGAL_MAT',     // fungal
  'NANITE_SWARM',   // nanite
];

/**
 * How much of its own emissive a weapon adds to each channel when drawn.
 *
 * Mirrors `drawWeapons`, which pushes `pr + emis * 0.6, pg + emis * 0.4,
 * pb + emis * 0.2`. It lives here because the *colour decision* below has to be
 * made against the colour that actually reaches the screen and not against the
 * albedo — LAVA's albedo is 111/255 and it draws at 194, which is the
 * difference between clearing a BONE body and colliding with it by 10.
 */
export const WEAPON_GLOW = { r: 0.6, g: 0.4, b: 0.2 } as const;

/** Rec. 709 luminance of a weapon *as drawn*, 0-255. Emissive included. */
export function weaponLuma(mat: MatId): number {
  const d = materialDef(mat);
  const e = d.emissive ?? 0;
  return 0.2126 * Math.min(255, d.color.r + e * WEAPON_GLOW.r * 255)
    + 0.7152 * Math.min(255, d.color.g + e * WEAPON_GLOW.g * 255)
    + 0.0722 * Math.min(255, d.color.b + e * WEAPON_GLOW.b * 255);
}

/** Rec. 709 luminance of a material's albedo, 0-255 — the body draws at this. */
function albedoLuma(mat: MatId): number {
  const c = materialDef(mat).color;
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

/**
 * The project's standing luminance bin, 40/255.
 *
 * Not a new number: `tests/materialGrain.test.ts` §1 sets it for a biome's
 * `shell` against its bulk, #60 measured eight of twelve biomes shipping a
 * shell identical to the rock it was cut into, and #79 found the same defect in
 * `accents` and in `primary`-against-`secondary`. A weapon against the body
 * swinging it is the same question one more file over.
 */
export const WEAPON_LUMA_BIN = 40;

/**
 * Where the element colour goes when the element colour cannot be seen.
 *
 * Ordered, and the order is the whole mechanism: the first rung clearing the
 * bin wins, so the result is deterministic and `BRASS` — what the game already
 * escaped to — keeps every case it was right about. Rungs are neutral,
 * non-elemental materials, spread along the luminance line so that a body of
 * any value has somewhere to send a blade: BRASS 141, OBSIDIAN 47, BONE 205,
 * GRAVEL 120, VOIDGLASS 18. The catalogue only reaches the first two (31 and 10
 * of the 41 substitutions); the other three are there because "the ladder ran
 * out" is a silent failure and the last rung is unconditional.
 */
const FALLBACK_LADDER: readonly (keyof typeof MAT)[] = [
  'BRASS', 'OBSIDIAN', 'BONE', 'GRAVEL', 'VOIDGLASS',
];

/**
 * The weapon's material: its element's, unless that cannot be seen against the
 * body swinging it.
 *
 * **The rule this replaces was an identity check** — `game.ts` compared the
 * chosen blade material against the wielder's three tissue slots with `===` and
 * escaped to BRASS on a match. That is exactly the defect #79 catalogued one
 * file over: identity is not legibility, and the eye's bar is a 40/255
 * luminance step. Measured across the 12 damage types × 17 archetypes:
 *
 *     collisions the identity rule caught                  2  of 204
 *     collisions inside the 40/255 bin                    41  of 204
 *
 * — and the escape hatch was inside the bin itself, 4.4 from STEEL and 5.4 from
 * NANITE_DUST, so `possessed-armor` (core STEEL) hit the identity rule and was
 * sent to a colour 4.4/255 from the armour it was escaping. That is the whole
 * argument for a ladder rather than a constant.
 *
 * **Scored against the core tissue and not all three**, which is #79's own
 * reasoning: it scored a vein against the *bulk* it is painted into and
 * explicitly declined to score it against every slot in the palette, because
 * scoring an adjacency the generator does not produce evicts good authoring to
 * fix a meeting that does not happen. A body is read as its dominant material
 * — `composition` is authored largest-first, 0.40–0.75 of the figure — and the
 * 10-20 % accents are pixels, not the silhouette. Scoring all three instead
 * takes the collision count from 41 to 100 and would repaint half the
 * catalogue; §3 reports those pairs rather than hiding the choice.
 */
export function bladeMatForDamage(damageType: number, coreTissue?: MatId): MatId {
  const mat: MatId = MAT[BLADE_MAT_BY_DAMAGE[damageType] ?? 'STEEL'];
  if (coreTissue === undefined || coreTissue === 0) return mat;
  const body = albedoLuma(coreTissue);
  if (Math.abs(weaponLuma(mat) - body) >= WEAPON_LUMA_BIN) return mat;
  for (const name of FALLBACK_LADDER) {
    const alt: MatId = MAT[name];
    if (Math.abs(weaponLuma(alt) - body) >= WEAPON_LUMA_BIN) return alt;
  }
  // Unreached by the shipping catalogue, and the last rung is returned rather
  // than the element's so that "no rung cleared" is at least a *different*
  // colour from the body. §3 asserts the ladder is never exhausted.
  return MAT[FALLBACK_LADDER[FALLBACK_LADDER.length - 1]];
}

/**
 * One melee class's drawing, as a transform of the baseline.
 *
 * `length` and `thickness` are multiples of the two scalars `visualForClass`
 * derives from `reach` and `weight`, so a row says the *proportions* and the
 * weapon's own stats say the size. Nothing here is a pixel.
 */
interface MeleeShape {
  style: SwingStyle;
  /** × `base`, which is the compressed reach. */
  length: number;
  /**
   * × `thick`, which is the compressed weight — or `{ fixed }` in body units
   * for a class whose weight must not thicken it. Two want that: a whip and a
   * chain are the width of the cord they are made of, and a heavy whip is a
   * heavier *crack*, not a fatter rope. A union rather than a second field, so
   * a row cannot carry a multiplier that does nothing.
   */
  thickness: number | { readonly fixed: number };
  segments: number;
  profile: WeaponProfile;
}

/**
 * The melee family — #111.
 *
 * **The claim, narrowly.** `docs/22` measured nine motion families and #88/#89/
 * #91 each added one that was genuinely inexpressible, so ten styles is not
 * over-authoring. But *within the hand-pivoted melee core the differences were
 * decorative*: `arc` served sword, axe and greatblade identically and `jab`
 * served dagger, claw and gauntlet identically — six classes, two curves, and
 * the only thing separating them was `length` and `thickness`. That is where a
 * parametric family pays, and it is what this table is.
 *
 * The playtest verdict was that **the Anchor reads closest to what a sword
 * should be**, and the reason is in `WeaponVisual.profile`'s note: the anchor
 * was the only melee entry with internal structure, and it had it by accident.
 * So the sword is promoted to the baseline *with the anchor's treatment* — a
 * real outline along its length — and the rest of the core is read as a
 * transform of it. The rows below are ordered to be read that way: the sword
 * first, then its neighbours as deltas.
 *
 * ---------------------------------------------------------------------------
 * **WHAT IS DELIBERATELY NOT PARAMETRIC, AND MUST NOT BE COLLAPSED.**
 *
 * `rotary` (scythe), `nova` (living), `halo` (chain) and `whip` are **not**
 * variations on `arc` with different numbers, and #111 is not licence to make
 * them so. Each was added against a *measured hitbox that disagreed with a
 * hand-pivoted arc*:
 *
 *   - `reaper_scythe`'s own hitbox is `arc halfAngle 2.6`, its heavy is `3.14`
 *     — a full circle centred on the wielder — and its description says it hits
 *     behind you on every swing (#88).
 *   - `flail_chain`'s base hitbox is `ring radius 26 halfAngle 3.14`, an
 *     annulus around the wielder, which is not a whip crack (#88).
 *   - `nova` is **[ours]** and radial: the radius is the motion and the angle is
 *     nearly incidental, which no hand-pivoted curve draws (#91).
 *   - `whip` is the only curve whose *curl* carries the read, and #101 measured
 *     that our node lengths cannot afford the alternative.
 *
 * A row here can change a weapon's proportions and its outline. It cannot make
 * one of those four into an `arc`, because the drawing would then disagree with
 * the box that does the damage.
 *
 * **AND WHAT IS NOT AN AXIS YET.** #111 lists "rotation offset" alongside scale
 * and profile. It is not here, and the reason is #38's rule, which this change
 * is bound by twice over: a carry angle alters the *drawn motion*, the profile
 * alters the *drawn shape*, and landing both at once makes any regression
 * unattributable. The field arrives with the class that needs it, in its own
 * commit, against its own before/after.
 */
const MELEE_FAMILY: Readonly<Record<string, MeleeShape>> = {
  // ---- the baseline ------------------------------------------------------
  //
  // Everything below is a delta from this row.
  sword: { style: 'arc', length: 1.1, thickness: 1, segments: 1, profile: 'blade' },

  // ---- the hand-pivoted core: the same two curves, transformed -----------
  //
  // `arc` and `jab` are untouched by #111 and still shared exactly as they
  // were. What used to be six switch arms differing only in two scalars is six
  // rows differing in two scalars and an outline.
  greatblade: { style: 'arc', length: 1.35, thickness: 1.5, segments: 1, profile: 'blade' },
  // Same curve as the sword at +5 % length, and the mass moved to the head.
  // `bearded` is an approximation: a real bit hangs off the axis and a profile
  // is symmetric about it — see `WeaponProfile`.
  axe: { style: 'arc', length: 1.15, thickness: 1.35, segments: 1, profile: 'bearded' },
  // The three `jab` classes are the sharpest case #111 makes: they resolve to
  // *identical* scalars, so before #113 they were the same drawing three times
  // over, and the profile is the entire difference between them.
  dagger: { style: 'jab', length: 0.62, thickness: 0.9, segments: 1, profile: 'fang' },
  // Three prongs, drawn as three swells on one axis — the honest limit of a
  // profile, and stated as such on `WeaponProfile`.
  claw: { style: 'jab', length: 0.62, thickness: 0.9, segments: 1, profile: 'talons' },
  // The one melee weapon with no point and no edge. A blunt block on a wrist is
  // the whole silhouette.
  gauntlet: { style: 'jab', length: 0.62, thickness: 0.9, segments: 1, profile: 'fist' },

  // ---- outside the core: a different curve, and a reason for it ----------
  hammer: { style: 'slam', length: 1.05, thickness: 1.7, segments: 1, profile: 'block' },
  spear: { style: 'thrust', length: 1.25, thickness: 0.8, segments: 1, profile: 'spike' },
  // Split from `spear` by profile only — same curve, same scalars. The two
  // resolve to 32.8 × 1.39 and 38.2 × 1.92, which is 14 % of the run and
  // nothing the eye can separate mid-swing; a lance's vamplate is the one
  // feature that says lance rather than long spear, and it costs a row.
  lance: { style: 'thrust', length: 1.25, thickness: 0.8, segments: 1, profile: 'couched' },
  // #100. `thrust` is documented as "juts straight out and retracts", which is
  // literally the anchor's motion: the head is cast out on the chain and hauled
  // back. No new `SwingStyle` — a curve is added when an existing one is
  // *wrong*, and this one is exactly right; what differs is the object on the
  // end of it. A short flexible run of segments reads as chain rather than as
  // pole, and the head is the heaviest thing drawn in the catalogue.
  //
  // **#113: that head was a claim, not a drawing.** `thickness * 1.6` made the
  // whole run 1.6× thicker and then tapered it linearly like everything else,
  // so the anchor had no head at all — it was the widest stick. It read anyway,
  // because five segments gave it internal structure nothing else had, and that
  // accident is what the playtest was praising. `fluke` is the head the
  // paragraph above always described.
  anchor: { style: 'thrust', length: 1.5, thickness: 1.6, segments: 5, profile: 'fluke' },
  // "Echo of the First Blade" — three passes is the fiction, and at 135 ms
  // (light1) to 245 ms (heavy) it is the fastest melee node that still leaves
  // ~2.7 rendered frames per pass at 60 Hz. The dagger and the claw that #89
  // nominated resolve to 58 ms and 67 ms, i.e. 1.2 and 1.3 frames per pass;
  // that is the smear the issue predicted, so they keep `jab`.
  //
  // `leaf` and not `blade`: at the catalogue's own numbers the Echo resolves to
  // 24.1 × 1.91 against the Ashen Longsword's 23.3 × 1.82, so sharing the
  // sword's outline would have made the two weapons the same drawing to within
  // 4 % — the exact defect #113 is about, reintroduced by a shared profile
  // instead of by a missing one. A leaf blade, widest two thirds along, is also
  // what "the First Blade" should be.
  relicBlade: { style: 'flurry', length: 1.1, thickness: 1, segments: 1, profile: 'leaf' },
  staff: { style: 'spin', length: 1.15, thickness: 1, segments: 1, profile: 'shaft' },
  // Same curve, same scalars as the staff — `spin` is right for both, and a
  // pole that bores is not a pole. The flights are the difference.
  drill: { style: 'spin', length: 1.15, thickness: 1, segments: 1, profile: 'auger' },

  // ---- the four measured families. DO NOT COLLAPSE — see the note above --
  scythe: { style: 'rotary', length: 1.15, thickness: 1.35, segments: 1, profile: 'crescent' },
  living: { style: 'nova', length: 1.05, thickness: 1.2, segments: 3, profile: 'sinew' },
  whip: { style: 'whip', length: 1.9, thickness: { fixed: 1.0 }, segments: 9, profile: 'cord' },
  chain: { style: 'halo', length: 1.6, thickness: { fixed: 1.3 }, segments: 7, profile: 'linked' },
};

/** Per-class motion and proportions. Reach scales it; class chooses the verb. */
export function visualForClass(
  cls: string,
  reach: number,
  weight: number,
  mat: MatId,
): WeaponVisual {
  // Character height is 22 units. Weapons should be big — noticeably bigger
  // than the wielder — but `reach` is a *hitbox* number: a spear's 36 fed in
  // raw produced a weapon three body-heights long that crossed the screen.
  // Compressing it keeps the class ordering (spear > sword > dagger) while
  // landing everything in a readable 20–40 unit band.
  const base = 14 + reach * 0.36;
  const thick = 0.9 + weight * 2.2;

  // `hasOwnProperty` and not a bare index: a `switch` is total by construction
  // and an object lookup is not — `MELEE_FAMILY['toString']` is a function, and
  // `cls` arrives here as a plain `string` off the catalogue.
  const row = Object.prototype.hasOwnProperty.call(MELEE_FAMILY, cls)
    ? MELEE_FAMILY[cls]
    : undefined;
  // Shields and every projectile class. Draws nothing, so the profile is
  // unreachable — `taper` because a shape nobody sees should be the one that
  // changes nothing if it is ever seen.
  if (!row) return { style: 'none', length: 0, thickness: 0, mat, segments: 1, profile: 'taper' };

  return {
    style: row.style,
    length: base * row.length,
    thickness: typeof row.thickness === 'number' ? thick * row.thickness : row.thickness.fixed,
    mat,
    segments: row.segments,
    profile: row.profile,
  };
}

export interface SwingPose {
  /** Base angle, radians, authored facing right (+x), +y down. */
  angle: number;
  /** 0..1 of full length. */
  extend: number;
  /**
   * Per-segment angular lag. 0 = rigid. Positive values curl the weapon back
   * along its length, which is what makes a whip look like rope.
   */
  curl: number;
  /** Whether to draw at all this frame. */
  visible: boolean;

  /**
   * False — the default and what all seven hand-pivoted styles leave it — means
   * the weapon turns about the hand joint and `pivotX`/`pivotY` are ignored.
   * True moves the centre of rotation onto the body (#88).
   *
   * **Why a flag and not the bare offset #88 asked for.** The issue proposed
   * `pivotX`/`pivotY` in body units as a displacement *from the hand*, default
   * `(0,0)`. That cannot name the body centre, because the hand is not a fixed
   * point during an attack: measured off `poseSkeleton` with `Pose.ATTACK` on
   * the humanoid, `handX` runs +4.73 → −3.35 and `handY` −20.56 → −9.59 over
   * `attack01 ∈ [0,1]`, i.e. the grip travels 8.1 × 11.0 units on a body 24.5
   * units tall. A constant offset from that wanders by half a body, so an orbit
   * authored against it would not orbit anything — it would follow the arm, and
   * the three families this field exists for would still be inexpressible.
   *
   * So the displaced pivot is expressed in the *body's own frame*, which is the
   * frame `handX`/`handY` are already in, and it is still anchored to the
   * character rather than floating in the world. It is an offset from the
   * body, not from nothing.
   *
   * **What this field is not.** #88 justified it by three families docs/22 §3
   * catalogued as body-pivoted. #91 then measured a second, longer source and
   * found — **[inferred]**, consistently across every instance stepped through —
   * that *the weapon does not move*: at 170.74–171.04 s the Moon rod's normal
   * attack sends a blue crescent around Alucard over 233 ms while the rod stays
   * small and at the hand. Those three families are an **effect with its own
   * centre and lifetime**, not a weapon swung about the body.
   *
   * That does not retire this field, and it does change what it claims. A
   * displaced pivot is honestly right for a weapon that *really does* orbit its
   * wielder, and the catalogue has one: `flail_chain`, whose base hitbox is a
   * ring around the wielder. It is defensible for `reaper_scythe`, which hits
   * behind you by design. It is **[ours]** for `nova`, which reproduces nothing
   * — SotN's radial discharge is an effect and we are drawing a weapon.
   *
   * An effect emitter — a thing with its own centre, lifetime and art, not
   * parented to the grip — is the honest way to reach the rest, and it is
   * deliberately **not** built here. It is a larger job, it is also what the
   * hand-off family needs, and it deserves its own issue rather than being
   * smuggled into this field.
   *
   * **That issue was #101, it was tried, and the answer came back no.** Not on
   * fidelity — the mechanism is still what the source does — but on frames.
   * `tests/effectStudy.test.ts` renders `halo` both ways at `flail_chain`'s own
   * node lengths, and an effect made of N identical objects needs about
   * `43 ms x N` before the eye stops matching each object to the wrong
   * neighbour and reading the ring as turning backwards. The source's 8–12
   * orbs therefore want 347–520 ms; our melee nodes have a median of 147 ms,
   * and of the 21 nodes across the three styles below exactly one is long
   * enough. Slowing the effect below the alias only trades a ring that turns
   * the wrong way for one that visibly does not move. So this field is not a
   * placeholder for something better that is coming — at our durations it is
   * the better drawing, because one connected shape carries its own
   * correspondence and N blobs do not. docs/22 §7.1 has the arithmetic.
   */
  bodyPivot: boolean;
  /**
   * Where the weapon turns, when `bodyPivot` is set. **Fractions of the body's
   * own height**, so one curve fits a 15-unit quadruped and a 32-unit tower
   * without knowing either: `x` from the centre line, `y` from the feet with
   * **+y down**, matching `Skeleton.handY`'s sign. The body centre is therefore
   * `(0, -0.5)`.
   *
   * A function of `t` like everything else here — `halo` moves its pivot around
   * the figure, which is what "circles the body at a fixed radius" means.
   */
  pivotX: number;
  pivotY: number;
}

const out: SwingPose = {
  angle: 0, extend: 0, curl: 0, visible: false,
  bodyPivot: false, pivotX: 0, pivotY: 0,
};

/**
 * Half a body height above the feet, `+y` down: the centre of the figure, and
 * the pivot docs/22 §3 measured for S1, S4 and S7.
 */
const BODY_CENTRE_Y = -0.5;

/**
 * Turns in one `rotary`, measured (docs/22 §3, S1: "~2.5 turns total").
 *
 * Worth a sanity check against our own clock, because ours is not SotN's: the
 * scythe's nodes resolve to 153–278 ms, so 2.5 turns is 3.7–6.7 rendered frames
 * per turn at 60 Hz. The shipping `spin` already runs 360° inside the staff's
 * 90 ms node, which is 5.4 frames per turn — so this is within a factor the
 * game already ships, and it is the *lobe* count below that had to give.
 */
const ROTARY_TURNS = 2.5;

/**
 * Radius lobes per `rotary`. **[ours]**, and the one place this file knowingly
 * departs from the measurement: docs/22 §3 counted 8–9 lobes on S1's trail, and
 * 9 lobes inside a 153 ms node is 1.0 rendered frame per lobe — a pulse nobody
 * can see, which aliases into noise rather than reading as a rosette. Three
 * gives 3.1 frames per lobe at the scythe's light1 and 5.6 at its heavy.
 */
const ROTARY_LOBES = 3;

/** `halo`'s orbit radius, fractions of body height. */
const HALO_RADIUS = 0.4;

/**
 * `flurry`'s three passes (docs/22 §3, S3: 3 passes, 49° of angular extent).
 *
 * The two numbers below are the 49°: each pass sweeps `±FLURRY_HALF` about a
 * centre that steps down by `FLURRY_DRIFT` per pass ("repeated near-horizontal
 * slashes at stacked heights"), so the extent is `2 * (HALF + DRIFT)` = 0.86
 * rad = 49.3°. Every pass crosses the forward axis, which is what makes them
 * passes rather than three wiggles on one side.
 */
const FLURRY_PASSES = 3;
const FLURRY_HALF = 0.3;
const FLURRY_DRIFT = 0.13;

/**
 * Where each style's sweep is centred — issue #126, and the whole of it.
 *
 * **The problem, restated from the measurement rather than from the report.**
 * A style's curve is authored as a *shape*, not as a bearing: `arc` runs −2.5 →
 * 0.75 rad, so at peak extension its blade sits 50° **above** level. The aim is
 * a rotation added on top and it is exactly right — `drawnAim.test.ts` §1 pins
 * it to nine decimal places — but it rotates a pose that was never pointing
 * forward. Aim up and the blade lands at −140°: past vertical, behind the head.
 * That is "aiming up does not read as up", and no amount of rotation fixes it,
 * because the rotation was never the wrong part.
 *
 * So the pose is re-centred: each style's drawn tip, at peak extension, with no
 * aim held, now points **level**. Aim then lands it on the aim, and the eight
 * directions read as themselves instead of as themselves plus a per-style bias.
 * The bias, before this, per style — the number subtracted below:
 *
 * ```
 *   arc  -50.1°   jab  -5.5°   thrust  +2.9°   slam  -93.8°   rotary  0.0°
 *   whip -25.8°   halo -59.1°  spin   +90.0°   nova  -63.3°   flurry  0.0°
 * ```
 *
 * **Why the number is the *tip* and not the pose angle.** Four styles are
 * flexible — `whip` 9 segments, `halo` 7, `nova` 3, `thrust`'s anchor 5 — and a
 * curled weapon does not point along its first segment. `whip`'s pose angle at
 * peak is +31.5° while its tip is at −25.8°; centring on the pose angle would
 * have moved it from 26° off to 57° off, i.e. made the one style that already
 * read acceptably worse. Adding a constant to `angle` rotates every segment and
 * therefore rotates the whole polyline, so the constant that zeroes the tip is
 * the tip's own angle, and that is what these are.
 *
 * **It is a constant per style, and that is only safe while each style's
 * geometry is one geometry.** The tip depends on `segments`, so two weapons of
 * one style with different segment counts would need different constants. Today
 * each flexible style has exactly one weapon and `thrust`'s five-segment anchor
 * has zero curl at peak, so one number per style is exact. `drawnAim.test.ts`
 * re-derives all ten from the shipped geometry of **every** weapon that draws,
 * so adding a second whip with a different spine turns that red rather than
 * quietly biasing it.
 *
 * **Three styles are full orbits and are re-centred anyway.** `spin`, `rotary`
 * and `halo` travel 360°, so "where it points" is arguably not what the eye
 * reads off them. They are included because the alternative is an exemption
 * list, because for `spin` and `rotary` the change is a pure phase shift of a
 * complete turn — nothing is lost — and because `spin` was the worst offender
 * in the table at a flat 90°, drawing level as *straight down*. For `halo` the
 * constant is a lead angle on top of the orbit tangent; the tangent was
 * **[ours]** to begin with (docs/22 §3 measured the path, not the weapon's
 * orientation on it) and the orbit itself, which is what the ring hitbox
 * corresponds to, is untouched.
 *
 * **`thrust` is left at zero, and it is the only exemption.** Its bias is
 * +2.9°, already fifteen times inside the band anything reads as, and the 0.05
 * rad that would cancel it is exactly where its curve *saturates*: the angle
 * lerps −0.35 → 0.05 over the first half and holds 0.05 for the second. Centring
 * it would park the whole back half of the thrust precisely on the forward axis
 * and turn the single crossing docs/22 measured for the style into a tangency —
 * `tests/weaponMotion.test.ts` counts that crossing as a design axis (#89). A
 * 2.9° readability gain is not worth deleting a measured property of the
 * motion, so the number stays 0 and the residual is asserted rather than fixed.
 *
 * This is presentation only. `fireOrSwing` derives the hitbox from `h.aim` and
 * has never read a pose; what changes is that the drawn weapon and the hitbox
 * now agree at peak instead of disagreeing by the style's bias.
 */
const SWING_CENTRE: Readonly<Record<SwingStyle, number>> = {
  arc: -0.875,
  jab: -0.09575,
  thrust: 0,
  slam: -1.636776859504132,
  rotary: 0,
  whip: -0.45,
  halo: -1.0307963267948966,
  spin: 1.5707963267948966,
  flurry: 0,
  nova: -1.1046,
  none: 0,
};

/**
 * Resolve the swing pose for a style at progress `t` (0..1 through the attack).
 *
 * Returns a shared object — read it before the next call.
 */
export function swingPose(style: SwingStyle, t: number): SwingPose {
  out.curl = 0;
  out.visible = true;
  // Reset every frame, like `curl`. A shared object that only some branches
  // write is a shared object that leaks last frame's pose into this one.
  out.bodyPivot = false;
  out.pivotX = 0;
  out.pivotY = 0;

  switch (style) {
    case 'arc': {
      // Starts cocked behind the head, sweeps forward and down past the feet.
      // The ease makes the middle of the arc the fastest part, which is where
      // the hit lands — the motion and the hitbox agree.
      const e = easeInOut(t);
      out.angle = lerp(-2.5, 0.75, e);
      out.extend = 1;
      break;
    }

    case 'slam': {
      // Slow wind-up, violent drop. Heavy weapons should look committed.
      const e = t < 0.45 ? (t / 0.45) * 0.25 : 0.25 + easeIn((t - 0.45) / 0.55) * 0.75;
      out.angle = lerp(-2.7, 1.45, e);
      out.extend = 1;
      break;
    }

    case 'thrust': {
      // Angle barely changes; the *length* is the motion.
      out.angle = lerp(-0.35, 0.05, Math.min(1, t * 2));
      out.extend = t < 0.5 ? easeOut(t / 0.5) : 1 - easeIn((t - 0.5) / 0.5) * 0.55;
      break;
    }

    case 'jab': {
      out.angle = lerp(-0.5, 0.2, easeOut(t));
      out.extend = t < 0.35 ? easeOut(t / 0.35) : 1 - (t - 0.35) / 0.65 * 0.4;
      break;
    }

    case 'whip': {
      // Three beats: draw back, crack forward, trail. The curl unwinds as the
      // whip straightens, so the tip arrives last — that lag IS the read.
      if (t < 0.35) {
        const u = t / 0.35;
        out.angle = lerp(-0.4, -2.6, easeOut(u)); // wind back over the shoulder
        out.extend = 0.45 + u * 0.3;
        out.curl = 0.55 * u;
      } else {
        const u = (t - 0.35) / 0.65;
        out.angle = lerp(-2.6, 0.55, easeIn(u)); // crack forward
        out.extend = 0.75 + easeOut(u) * 0.25;
        out.curl = 0.55 * (1 - easeOut(u)) - 0.25 * u;
      }
      break;
    }

    case 'spin': {
      out.angle = -Math.PI / 2 + t * Math.PI * 2;
      out.extend = 1;
      break;
    }

    case 'flurry': {
      // Three passes inside one motion — docs/22 §3, S3, and #89. `spin` and
      // `whip` both travel further than this; what none of the other nine do
      // is cross the forward axis more than once.
      //
      // The retraction is not decoration. Without it the three passes join up
      // into one continuous zig-zag and read as a single shaky sweep; the blade
      // has to visibly come back before it goes out again for the eye to count
      // three of anything.
      const u = Math.min(FLURRY_PASSES - 1e-9, t * FLURRY_PASSES);
      const k = Math.floor(u);
      const f = u - k;
      const centre = -FLURRY_DRIFT + k * FLURRY_DRIFT;
      out.angle = centre + lerp(-FLURRY_HALF, FLURRY_HALF, easeInOut(f));
      out.extend = 0.45 + 0.55 * Math.sin(Math.PI * f);
      break;
    }

    case 'rotary': {
      // S1. The blade orbits the whole figure, hilt at the chest, radius
      // pulsing into lobes. `spin` is the near neighbour and the difference is
      // exactly the two things #88 and docs/22 §6 name: where it turns, and how
      // far it goes.
      out.angle = -Math.PI / 2 + t * Math.PI * 2 * ROTARY_TURNS;
      // An envelope at both ends, so the weapon grows out of the body and
      // collapses back into it instead of appearing at full radius. `arc` and
      // `spin` can pop because their hilt is at the hand and the hand is on the
      // silhouette; a body pivot puts the hilt inside the torso, where a pop
      // reads as the character being stabbed by their own weapon.
      const env = Math.min(1, t / 0.12) * Math.min(1, (1 - t) / 0.12);
      out.extend = (0.55 + 0.45 * Math.abs(Math.sin(t * Math.PI * ROTARY_LOBES))) * env;
      out.bodyPivot = true;
      out.pivotX = 0;
      out.pivotY = BODY_CENTRE_Y;
      break;
    }

    case 'nova': {
      // **[ours].** docs/22 S4's shape, but not its mechanism: #91 found the
      // source's radial discharges are effects and the weapon never leaves the
      // hand, so this is a limb erupting from a torso because a symbiote should
      // do that, not because Alucard does. Kept anyway — the user asked to
      // exceed the reference, and this is a motion none of the other nine draw.
      //
      // Radial, so the *radius* is the motion and the angle is nearly
      // incidental — the mirror image of `thrust`, which is the same trade made
      // at the hand. 122° is the measured extent of the wider of the two events.
      out.angle = lerp(-1.6, 0.53, easeInOut(t));
      out.extend = t < 0.3 ? easeOut(t / 0.3) : 1 - easeIn((t - 0.3) / 0.7);
      // Segments plus a little curl so an organic weapon lashes rather than
      // telescopes; a one-segment weapon ignores this and just spikes.
      out.curl = 0.16 * (1 - t);
      out.bodyPivot = true;
      out.pivotX = 0;
      out.pivotY = BODY_CENTRE_Y;
      break;
    }

    case 'halo': {
      // S7: 360°, one orbit, fixed radius. The pivot itself goes round — this
      // is the family that needs a *moving* body pivot, and the reason
      // `pivotX`/`pivotY` are functions of `t` rather than constants on the
      // style.
      //
      // **The one place a displaced pivot is unambiguously honest.** #91 found
      // SotN's ring is an effect; a flail on a chain is not, it genuinely
      // travels around its wielder, and `flail_chain`'s base hitbox is already
      // `ring radius 26 halfAngle 3.14`. Here the drawing and the hitbox agree
      // and nothing is being imitated that ought to be an effect.
      // Starts at the front of the body and drops, so the first half of the
      // orbit is in front of the wielder where the ring hitbox opens, and the
      // half spent behind the figure is the second half.
      const th = t * Math.PI * 2;
      out.pivotX = Math.cos(th) * HALO_RADIUS;
      out.pivotY = BODY_CENTRE_Y + Math.sin(th) * HALO_RADIUS;
      // Tangent to the orbit, leading it. docs/22 §3 measured the path and not
      // the weapon's orientation on it, so this is **[ours]**: a flail held out
      // along the tangent wraps the figure, where a radial one reads as a spoke
      // and is already what `rotary` draws.
      out.angle = th + Math.PI / 2;
      out.extend = 0.5;
      // A 7-segment chain at this length bends about a fifth of a radian per
      // segment, which closes the tangent back around the body — the ring in
      // "halo". A rigid weapon ignores it.
      out.curl = 0.18;
      out.bodyPivot = true;
      break;
    }

    case 'none':
    default:
      out.visible = false;
      out.extend = 0;
      out.angle = 0;
      break;
  }
  // The re-centring, applied once at the end rather than folded into ten
  // curves. Each `case` above stays readable as the shape docs/22 measured —
  // `arc` is still "−2.5 to 0.75, cocked behind the head to past the feet" —
  // and the bearing that shape is hung on is one table, in one place, next to
  // the reason it exists. `none` subtracts zero and draws nothing anyway.
  out.angle -= SWING_CENTRE[style];
  return out;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
export function easeIn(t: number): number {
  return t * t;
}
export function easeOut(t: number): number {
  const u = 1 - t;
  return 1 - u * u;
}
export function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - 2 * (1 - t) * (1 - t);
}
